import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Text, Animated } from 'react-native';
import DataTable from '../../../components/DataTable';
import FontFamily from '../../../constants/fonts';
import { formatNameFromEmail } from '../../ActionPlan/utils/actionPlanTransformers';
import { getPriorityDisplayText, getSourceDisplayText, getTypeDisplayText } from '../../ActionPlan/utils/formOptions';
// Utility function to format date consistently as "Jan 22, 2026"
// Parse UTC date string WITHOUT timezone conversion
const formatDateToDDMMYYYY = (dateString?: string): string => {
  if (!dateString) return '';
  
  try {
    // Parse ISO string directly without timezone conversion
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const year = isoMatch[1];
      const month = parseInt(isoMatch[2], 10);
      const day = parseInt(isoMatch[3], 10);
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      return `${monthNames[month - 1]} ${day}, ${year}`;
    }
    
    return dateString;
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString;
  }
};

// Column configuration for Top Opportunities
const columns = [
  { key: 'title', title: 'Title', flex: 3 },
  { key: 'assignedUser', title: 'Assigned User', flex: 2 },
  { key: 'status', title: 'Status', flex: 1.5 },
  { key: 'targetDate', title: 'Target Date', flex: 1.5 },
  { key: 'source', title: 'Source', flex: 1.5 },
];

// Skeleton component for table row loading state
const TableRowSkeleton: React.FC = () => {
  const opacity = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [opacity]);

  return (
    <View style={styles.skeletonRow}>
      <Animated.View style={[styles.skeletonCell, styles.skeletonCellLarge, { opacity }]} />
      <Animated.View style={[styles.skeletonCell, styles.skeletonCellMedium, { opacity }]} />
      <Animated.View style={[styles.skeletonCell, styles.skeletonCellSmall, { opacity }]} />
      <Animated.View style={[styles.skeletonCell, styles.skeletonCellSmall, { opacity }]} />
      <Animated.View style={[styles.skeletonCell, styles.skeletonCellSmall, { opacity }]} />
    </View>
  );
};

export interface TopOpportunityItem {
  ID: number;
  TITLE: string;
  SOURCE: string;
  TYPE: string;
  PRIORITY: string;
  STATUS: string;
  ASSIGNMENT_STATUS?: string;
  OWNER_GPID: string;
  OWNER_ROLE?: string; // Owner's role for delete permission check
  ASSIGNEE_GPID: string;
  OWNER_NAME?: string;
  ASSIGNEE_NAME?: string;
  TARGET_DATE?: string;
  START_DATE?: string;
  CREATED_AT?: string;
  UPDATED_AT?: string;
  DESCRIPTION?: string;
  INVOLVED_EMPLOYEE_GPIDS?: string;
  INVOLVED_EMPLOYEE_NAMES?: string;
  ACTION_PLAN_DESCRIPTION?: string;
  [key: string]: any;
}

export interface TopOpportunitiesPagination {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
}

interface TopOpportunitiesTableProps {
  data: TopOpportunityItem[];
  isLoading?: boolean;
  onViewDetailsPress?: (item: TopOpportunityItem) => void;
  error?: string;
  onFavoriteToggle?: (itemId: string) => void;
  onStatusChange?: (itemId: string, newStatus: string) => Promise<void>;
  maxRecordsToShow?: number;
  showFiveRecordsOnly?: boolean; // Custom prop to limit to 5 records, handled inside the table component
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

const TopOpportunitiesTable: React.FC<TopOpportunitiesTableProps> = ({ 
  data, 
  isLoading, 
  error,
  onViewDetailsPress,
  onFavoriteToggle,
  onStatusChange,
  maxRecordsToShow = 5,
  // Pagination props
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  showFiveRecordsOnly = false, // Custom prop to limit to 5 records, handled inside the table component
}) => {
  // Show loading state
  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.tableHeader}>
          {columns.map((col) => (
            <View key={col.key} style={[styles.headerCell, { flex: col.flex }]}>
              <Text style={styles.headerText}>{col.title}</Text>
            </View>
          ))}
        </View>
        <View style={styles.skeletonContainer}>
          {[...Array(5)].map((_, index) => (
            <TableRowSkeleton key={`skeleton-${index}`} />
          ))}
        </View>
      </View>
    );
  }

  // Show error state
  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.messageContainer}>
          <Text style={styles.errorText}>Error: {error}</Text>
        </View>
      </View>
    );
  }

  // Show empty state
  if (!data || data.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.messageContainer}>
          <Text style={styles.emptyText}>No top opportunities found</Text>
        </View>
      </View>
    );
  }

    // Handle view details button press - convert row data back to ActionPlanItem
  const handleViewDetailsPress = (rowData: any) => {
    if (onViewDetailsPress) {
      // Find the original ActionPlanItem by ID
      const originalItem = data.find(item => String(item.ID) === rowData.id);
      if (originalItem) {
        onViewDetailsPress(originalItem);
      }
    }
  };

  // Transform data to match DataTable expected format
   const transformedData = data.map((item) => ({
      id: String(item.ID),
      title: item.TITLE || '',
      assignedUser: formatNameFromEmail(item.ASSIGNEE_EMAIL, item.ASSIGNEE_GPID),
      // Show ASSIGNMENT_STATUS value if it's "declined", otherwise show STATUS
      status: item.ASSIGNMENT_STATUS?.toLowerCase() === 'declined' ? (item.ASSIGNMENT_STATUS || '') : (item.STATUS || ''),
      priority: getPriorityDisplayText(item.PRIORITY) || '',
      targetDate: formatDateToDDMMYYYY(item.TARGET_DATE),
      targetDateRaw: item.TARGET_DATE, // Keep raw date for sorting
      source: getSourceDisplayText(item.SOURCE) || '',
      isTemp:item.IS_TEMP ===1? true : false, // Mark as temporary if IS_TEMP is 1
      badgeText: item.STATUS === 'New' ? 'NEW' : undefined,
      isFavorite: item.TOP_OPPORTUNITY === 1 || item.TOP_OPPORTUNITY === true,
      // Include raw data for role-based status access control and delete permissions
      rawData: {
        ASSIGNEE_EMAIL: item.ASSIGNEE_EMAIL,
        OWNER_EMAIL: item.OWNER_EMAIL,
        LOCATION: item.LOCATION, // Action plan location for Site Leader access check
        OWNER_ROLE: item.OWNER_ROLE, // Owner's role for delete permission check
        ASSIGNMENT_STATUS: item.ASSIGNMENT_STATUS, // Check if assignment is declined
      },
      expansionData: {
        actionPlanOwner: formatNameFromEmail(item.OWNER_EMAIL, item.OWNER_GPID),
        type: getTypeDisplayText(item.TYPE) || '',
        startDate: formatDateToDDMMYYYY(item.START_DATE),
        source: getSourceDisplayText(item.SOURCE) || '',
        frontlineEmployee: item?.INVOLVED_EMPLOYEE_NAMES || '',
        actionPlan: item.ACTION_PLAN_DESCRIPTION || '',
      },
    }));

  return (
    <View style={styles.container}>
      <DataTable 
        columns={columns} 
        data={transformedData}
        onViewDetailsPress={handleViewDetailsPress}
        maxRecordsToShow={showFiveRecordsOnly ? 5 : 10} // Pass the prop to DataTable to limit records if needed
        showHeaderTopBorder={true}
        onStatusChange={onStatusChange}
        showFavoriteIcon={true}
        useCustomStarIcon={true}
        onFavoriteToggle={onFavoriteToggle}
        hideActionMenu={true}
      />
    </View>
  );
};

// Render ActionPlanDetails if navigating to it


const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    paddingHorizontal: 0,
  },
  messageContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#005CBC',
    fontFamily: FontFamily.regular,
  },
  errorText: {
    fontSize: 16,
    color: '#dc3545',
    fontFamily: FontFamily.regular,
  },
  emptyText: {
    fontSize: 16,
    color: '#6c757d',
    fontFamily: FontFamily.regular,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F7FAFC',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#E0E0E0',
  },
  headerCell: {
    justifyContent: 'center',
  },
  headerText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1C',
    fontFamily: FontFamily.medium,
  },
  skeletonContainer: {
    backgroundColor: '#FFFFFF',
  },
  skeletonRow: {
    flexDirection: 'row',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderColor: '#E0E0E0',
    gap: 12,
  },
  skeletonCell: {
    height: 16,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonCellLarge: {
    flex: 3,
  },
  skeletonCellMedium: {
    flex: 2,
  },
  skeletonCellSmall: {
    flex: 1.5,
  },
});

export default TopOpportunitiesTable;
